import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Lock, User, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signUp, signIn } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('signin');
  
  const [signInData, setSignInData] = useState({
    email: '',
    password: ''
  });
  
  const [signUpData, setSignUpData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: ''
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && user) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, location]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await signIn(signInData.email, signInData.password);
      
      if (error) {
        let errorMessage = language === 'en' 
          ? 'Invalid email or password' 
          : 'Email o contraseña inválidos';
          
        if (error.message.includes('Email not confirmed')) {
          errorMessage = language === 'en' 
            ? 'Please check your email and confirm your account' 
            : 'Por favor revisa tu email y confirma tu cuenta';
        }
        
        toast({
          title: language === 'en' ? 'Error' : 'Error',
          description: errorMessage,
          variant: 'destructive'
        });
      } else {
        toast({
          title: language === 'en' ? 'Welcome back!' : '¡Bienvenido de vuelta!',
          description: language === 'en' ? 'You have been signed in successfully.' : 'Has iniciado sesión exitosamente.'
        });
      }
    } catch (error) {
      toast({
        title: language === 'en' ? 'Error' : 'Error',
        description: language === 'en' ? 'An unexpected error occurred' : 'Ocurrió un error inesperado',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (signUpData.password !== signUpData.confirmPassword) {
      toast({
        title: language === 'en' ? 'Error' : 'Error',
        description: language === 'en' ? 'Passwords do not match' : 'Las contraseñas no coinciden',
        variant: 'destructive'
      });
      return;
    }

    if (signUpData.password.length < 6) {
      toast({
        title: language === 'en' ? 'Error' : 'Error',
        description: language === 'en' ? 'Password must be at least 6 characters' : 'La contraseña debe tener al menos 6 caracteres',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await signUp(signUpData.email, signUpData.password, signUpData.fullName);
      
      if (error) {
        let errorMessage = language === 'en' ? 'Failed to create account' : 'Error al crear la cuenta';
        
        if (error.message.includes('already registered')) {
          errorMessage = language === 'en' 
            ? 'An account with this email already exists' 
            : 'Ya existe una cuenta con este email';
        }
        
        toast({
          title: language === 'en' ? 'Error' : 'Error',
          description: errorMessage,
          variant: 'destructive'
        });
      } else {
        toast({
          title: language === 'en' ? 'Account created!' : '¡Cuenta creada!',
          description: language === 'en' 
            ? 'Please check your email to confirm your account.' 
            : 'Por favor revisa tu email para confirmar tu cuenta.'
        });
        setActiveTab('signin');
      }
    } catch (error) {
      toast({
        title: language === 'en' ? 'Error' : 'Error',
        description: language === 'en' ? 'An unexpected error occurred' : 'Ocurrió un error inesperado',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={language === 'en' ? 'Sign In - The Grass Depot' : 'Iniciar Sesión - The Grass Depot'}
        description={language === 'en' 
          ? 'Sign in to your account to save quotes and manage your landscaping projects.'
          : 'Inicia sesión en tu cuenta para guardar cotizaciones y gestionar tus proyectos de paisajismo.'
        }
      />
      
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link 
              to="/" 
              className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {language === 'en' ? 'Back to Home' : 'Volver al Inicio'}
            </Link>
            
            <h1 className="text-3xl font-bold text-foreground">
              {language === 'en' ? 'The Grass Depot' : 'The Grass Depot'}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {language === 'en' 
                ? 'Sign in to save quotes and manage your projects' 
                : 'Inicia sesión para guardar cotizaciones y gestionar proyectos'
              }
            </p>
          </div>

          <Card className="shadow-glow">
            <CardHeader className="text-center">
              <CardTitle>
                {language === 'en' ? 'Welcome' : 'Bienvenido'}
              </CardTitle>
              <CardDescription>
                {language === 'en' 
                  ? 'Sign in to your account or create a new one' 
                  : 'Inicia sesión en tu cuenta o crea una nueva'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="signin">
                    {language === 'en' ? 'Sign In' : 'Iniciar Sesión'}
                  </TabsTrigger>
                  <TabsTrigger value="signup">
                    {language === 'en' ? 'Sign Up' : 'Registrarse'}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="signin">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div>
                      <Label htmlFor="signin-email" className="flex items-center">
                        <Mail className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Email' : 'Correo electrónico'}
                      </Label>
                      <Input
                        id="signin-email"
                        type="email"
                        value={signInData.email}
                        onChange={(e) => setSignInData(prev => ({ ...prev, email: e.target.value }))}
                        required
                        placeholder={language === 'en' ? 'Enter your email' : 'Ingresa tu correo'}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="signin-password" className="flex items-center">
                        <Lock className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Password' : 'Contraseña'}
                      </Label>
                      <Input
                        id="signin-password"
                        type="password"
                        value={signInData.password}
                        onChange={(e) => setSignInData(prev => ({ ...prev, password: e.target.value }))}
                        required
                        placeholder={language === 'en' ? 'Enter your password' : 'Ingresa tu contraseña'}
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {language === 'en' ? 'Sign In' : 'Iniciar Sesión'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="signup">
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div>
                      <Label htmlFor="signup-name" className="flex items-center">
                        <User className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Full Name' : 'Nombre completo'}
                      </Label>
                      <Input
                        id="signup-name"
                        type="text"
                        value={signUpData.fullName}
                        onChange={(e) => setSignUpData(prev => ({ ...prev, fullName: e.target.value }))}
                        required
                        placeholder={language === 'en' ? 'Enter your full name' : 'Ingresa tu nombre completo'}
                      />
                    </div>

                    <div>
                      <Label htmlFor="signup-email" className="flex items-center">
                        <Mail className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Email' : 'Correo electrónico'}
                      </Label>
                      <Input
                        id="signup-email"
                        type="email"
                        value={signUpData.email}
                        onChange={(e) => setSignUpData(prev => ({ ...prev, email: e.target.value }))}
                        required
                        placeholder={language === 'en' ? 'Enter your email' : 'Ingresa tu correo'}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="signup-password" className="flex items-center">
                        <Lock className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Password' : 'Contraseña'}
                      </Label>
                      <Input
                        id="signup-password"
                        type="password"
                        value={signUpData.password}
                        onChange={(e) => setSignUpData(prev => ({ ...prev, password: e.target.value }))}
                        required
                        placeholder={language === 'en' ? 'Create a password' : 'Crea una contraseña'}
                      />
                    </div>

                    <div>
                      <Label htmlFor="signup-confirm" className="flex items-center">
                        <Lock className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Confirm Password' : 'Confirmar contraseña'}
                      </Label>
                      <Input
                        id="signup-confirm"
                        type="password"
                        value={signUpData.confirmPassword}
                        onChange={(e) => setSignUpData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        required
                        placeholder={language === 'en' ? 'Confirm your password' : 'Confirma tu contraseña'}
                      />
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {language === 'en' ? 'Create Account' : 'Crear Cuenta'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AuthPage;